import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, ValidationErrors, Validators } from '@angular/forms';

import { Client } from 'src/providers/client/client';

import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { WindowService } from 'src/shared/providers/window.service';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { MarketingChannelsService, AddressListing } from 'src/shared/services/marketing-channels.service';
import { ListingSetupService, AddressListingSetupState, NewChannelListing } from 'src/shared/services/listing-setup.service';
import { FeatureGateService } from 'src/shared/services/feature-gate.service';
import { AccountManagerService } from 'src/shared/services/account-manager.service';
import { DemoBlockService } from 'src/shared/services/demo-block.service';
import { Util } from 'src/shared/util/util';

import { PLATFORM_MARK_LOGOS, STR_CHANNEL_PLATFORMS, formatPlatformName, getContactPrompt, getPlatformIdLabel, getWaitingOnYou } from 'src/shared/constants/listing-platforms';

import { CheckboxModel } from 'src/tidy-ui-components/components/form/checkbox/checkbox.model';
import { SelectModel } from 'src/tidy-ui-components/components/form/select/select.model';

// 'all' asks about every address × channel on one page; 'single' is the mini version
// opened from a "Needs setup" row: one address, one channel, one question.
type SetupMode = 'all' | 'single';
type SetupState = 'loading' | 'error' | 'no-property' | 'form' | 'summary';

// "What are you listing?" — by-the-room listings aren't built yet, so picking a room
// shows the account-manager gate and the select snaps back to the entire home.
const ENTIRE_HOME_TARGET = 'entire';
const ROOM_TARGET = 'room';

export interface ListingSetupEntry {
  key: string;
  addressId: number;
  addressName: string;
  platform: string;
  platformLabel: string;
  logo: string;
  idLabel: string;
  checkboxItems: CheckboxModel[];
  // The checkbox: "I already have this listing".
  has: boolean;
  // A backend listing already exists, so the box is checked and can't be cleared
  // (disconnecting stays on the listing detail page); the ID stays editable.
  locked: boolean;
  existing: AddressListing | null;
  statusNote: string;
  // The client previously answered "no" (TIDY is setting it up).
  conciergeSetup: boolean;
  listingId: FormControl<string>;
  target: FormControl<string>;
  // Mirrors `target` for tidy-select's [selectedItem], so a rejected room pick can snap
  // the visible value back (the select only re-reads its control on init).
  targetItem: SelectModel;
  idError: string;
}

export interface ListingSetupGroup {
  addressId: number;
  name: string;
  city: string;
  entries: ListingSetupEntry[];
}

export interface ListingSetupSummaryItem {
  addressId: number;
  platform: string;
  platformLabel: string;
  logo: string;
  addressName: string;
  title: string;
  detail: string;
  cohostUrl: string | null;
  // How the account manager fits in (send them X / they'll send you Y); '' for Airbnb.
  contactPrompt: string;
}

/**
 * "Tell Us About Your Listings" — the single entry point behind every listings action
 * item (My Account, the Listings page, the concierge-assigned `listings` step).
 *
 * Form: every address with an Airbnb / VRBO / Booking.com checkbox each. Checked →
 * listing ID (+ entire home / room) and the listing is created as `connecting`.
 * Unchecked → TIDY sets it up: a metadata marker per address × channel plus a concierge
 * ticket. Vrbo / Booking.com listings the client DOES have also file a ticket, since
 * their handover is TIDY's work. Then the address's `listings` step is marked completed
 * and a summary page spells out what we're waiting on from the client vs. what we handle.
 */
@Component({
  selector: 'listings-setup',
  templateUrl: './listings-setup.component.html',
  styleUrls: ['./listings-setup.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  // Declared in SharedModule rather than standalone: it needs tidy-top-nav,
  // app-scroll-wrapper and the translate pipe (SharedModule-only), and it is imported
  // by SharedModule-declared callers (My Account, Success, the step navigation
  // service) — a standalone component importing SharedModule would close a module
  // cycle, which black-screens the app at startup.
  standalone: false
})
export class ListingsSetupComponent implements OnInit, OnDestroy {

  mode: SetupMode = 'all';
  state: SetupState = 'loading';
  groups: ListingSetupGroup[] = [];

  // Single mode: the one entry and the yes / no answer.
  entry: ListingSetupEntry | null = null;
  answer: 'yes' | 'no' | null = null;
  singleQuestion = '';

  submitted = false;
  submitting = false;

  readonly targetItems: SelectModel[] = [
    { value: ENTIRE_HOME_TARGET, viewValue: 'Entire home' },
    { value: ROOM_TARGET, viewValue: 'A room' },
  ];
  readonly cohostEmail = ListingSetupService.COHOST_EMAIL;

  summaryTitle = '';
  summaryIntro = '';
  summaryNeeded: ListingSetupSummaryItem[] = [];
  summaryHandled: ListingSetupSummaryItem[] = [];

  // Whatever the client has to send us (Vrbo booking list, review consent…) goes to their
  // account manager, so the summary offers call / email / text.
  managerName = AccountManagerService.DEFAULT.name;
  private managerEmail = AccountManagerService.DEFAULT.email;
  private managerPhone = AccountManagerService.DEFAULT.phone;

  private addressId: number | null = null;
  private platform: string | null = null;
  private returnRoute: string | null = null;
  private addresses: any[] = [];
  private listingsByAddress = new Map<number, AddressListing[]>();
  private setupState = new Map<number, AddressListingSetupState>();

  private client = inject(Client);
  private rightSidePanelService = inject(RightSidePanelService);
  private navCtrl = inject(CustomNavController);
  private storage = inject(TidyStorage);
  private channels = inject(MarketingChannelsService);
  private setupService = inject(ListingSetupService);
  private featureGate = inject(FeatureGateService);
  private accountManager = inject(AccountManagerService);
  private demoBlock = inject(DemoBlockService);
  private util = inject(Util);
  private fb = inject(FormBuilder);
  private cdr = inject(ChangeDetectorRef);
  windowService = inject(WindowService);

  async ngOnInit(): Promise<void> {
    this.rightSidePanelService.setDialogPageTitle('Tell Us About Your Listings');
    // The opener's subtitle (e.g. the property line of the Listings editor) would
    // otherwise linger in the dialog header.
    this.rightSidePanelService.setDialogPageSubtitle(null);
    const params = (await this.storage.retrieve('dialog-params')) || {};
    this.addressId = params.addressId ?? null;
    this.platform = params.platform ?? null;
    this.returnRoute = params.returnRoute ?? null;
    this.mode = this.platform && this.addressId ? 'single' : 'all';
    // navigateTo merges each caller's params over whatever is still stored, and only
    // closing the desktop panel clears them — on mobile they outlive the page. Drop the
    // two keys that decide what this page IS, so opening the full flow after a single
    // "Needs setup" row doesn't reopen that one channel's question.
    await this.storage.save('dialog-params', { ...params, platform: null, returnRoute: null });
    this.loadAccountManager();
    await this.load();
  }

  private async loadAccountManager(): Promise<void> {
    try {
      const manager = await this.accountManager.getDisplay();
      this.managerName = manager.name;
      this.managerEmail = manager.email;
      this.managerPhone = manager.phone;
      this.cdr.markForCheck();
    } catch {
      // Keep TIDY's defaults.
    }
  }

  ngOnDestroy(): void {
    this.rightSidePanelService.customBackAction$.next(null);
  }

  private async load(): Promise<void> {
    try {
      // Fresh read so a property added a moment ago is on the page.
      const all: any[] = (await this.client.getAddresses(true)) || [];
      this.addresses = this.mode === 'single' ? all.filter((a) => a.id === this.addressId) : all;
      if (!this.addresses.length) {
        this.state = 'no-property';
        return;
      }
      const ids: number[] = this.addresses.map((a) => a.id);
      const [listings, setupState] = await Promise.all([
        Promise.all(ids.map((id) => this.client.getAddressListings(id)
          .then((rows: any) => (Array.isArray(rows) ? rows : []) as AddressListing[])
          .catch(() => [] as AddressListing[]))),
        this.setupService.getSetupState(ids),
      ]);
      this.listingsByAddress = new Map(ids.map((id, index) => [id, listings[index]]));
      this.setupState = setupState;
      this.groups = this.addresses.map((address) => this.buildGroup(address));
      if (this.mode === 'single') {
        this.entry = this.groups[0]?.entries[0] ?? null;
        if (!this.entry) {
          this.state = 'no-property';
          return;
        }
        this.answer = this.entry.existing ? 'yes' : (this.entry.conciergeSetup ? 'no' : null);
        this.entry.has = this.answer === 'yes';
        this.singleQuestion = `Do you have a ${this.entry.platformLabel} listing for ${this.entry.addressName}?`;
      }
      this.state = 'form';
    } catch {
      this.state = 'error';
    } finally {
      this.cdr.markForCheck();
    }
  }

  private buildGroup(address: any): ListingSetupGroup {
    const name: string = address.address_name || address.address1;
    const platforms: readonly string[] = this.mode === 'single' ? [this.platform!] : STR_CHANNEL_PLATFORMS;
    const markers = this.setupState.get(address.id)?.conciergePlatforms ?? new Set<string>();
    const listings = this.listingsByAddress.get(address.id) || [];
    return {
      addressId: address.id,
      name,
      city: [address.city, address.add_state || address.state].filter(Boolean).join(', '),
      entries: platforms.map((platform) => {
        const existing = listings.find((l) => l.platform === platform) ?? null;
        const label = formatPlatformName(platform);
        const conciergeSetup = markers.has(platform);
        return {
          key: `${address.id}.${platform}`,
          addressId: address.id,
          addressName: name,
          platform,
          platformLabel: label,
          logo: PLATFORM_MARK_LOGOS[platform] || '',
          idLabel: getPlatformIdLabel(platform),
          checkboxItems: [{ value: platform, viewValue: label }],
          has: !!existing,
          locked: !!existing,
          existing,
          statusNote: existing
            ? `Already added · ${this.channels.formatStatus(platform, existing.status)}`
            : (conciergeSetup ? 'TIDY is creating your listing' : ''),
          conciergeSetup,
          listingId: this.fb.nonNullable.control(existing?.platform_id || '', [Validators.required, this.listingIdValidator(platform)]),
          target: this.fb.nonNullable.control(ENTIRE_HOME_TARGET),
          targetItem: this.targetItems[0],
          idError: 'Please enter your listing ID',
        };
      }),
    };
  }

  private listingIdValidator(platform: string) {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = (control.value || '').toString().trim();
      if (!value) { return null; }
      return this.channels.parseListingId(value, platform) === null ? { invalidListing: true } : null;
    };
  }

  private allEntries(): ListingSetupEntry[] {
    return this.groups.flatMap((g) => g.entries);
  }

  // ---------- Form interactions ----------

  onEntryChecked(entry: ListingSetupEntry, checked: boolean): void {
    if (entry.locked) { return; }
    entry.has = checked;
    this.cdr.markForCheck();
  }

  onTargetChange(entry: ListingSetupEntry, value: string): void {
    if (value !== ROOM_TARGET) { return; }
    // Room listings hand off to the account manager; the select stays on the entire home.
    // tidy-select writes the picked value into the control AFTER emitting, so the reset has
    // to wait a tick — and a fresh selectedItem object makes its display follow.
    setTimeout(() => {
      entry.target.setValue(ENTIRE_HOME_TARGET, { emitEvent: false });
      entry.targetItem = { ...this.targetItems[0] };
      this.cdr.markForCheck();
    });
    this.featureGate.gate();
  }

  selectAnswer(answer: 'yes' | 'no'): void {
    this.answer = answer;
    if (this.entry) { this.entry.has = answer === 'yes'; }
    this.submitted = false;
    this.cdr.markForCheck();
  }

  retry = () => {
    this.state = 'loading';
    this.cdr.markForCheck();
    this.load();
  };

  // Adding a property happens on the Overview page (its "Add a Property" card), not
  // inside this dialog.
  goToAddProperty = () => {
    if (this.windowService.isDesktopRes) {
      this.rightSidePanelService.closeRightSidePanel();
    }
    this.navCtrl.navigateForward('properties');
  };

  // ---------- Submit ----------

  submit = async () => {
    if (this.submitting) { return; }
    if (this.demoBlock.block()) { return; }
    this.submitted = true;
    if (this.mode === 'single' && !this.answer) {
      this.cdr.markForCheck();
      return;
    }
    const entries = this.allEntries();
    let invalid = false;
    for (const entry of entries) {
      if (!entry.has) { continue; }
      if (entry.listingId.invalid) {
        invalid = true;
        entry.idError = entry.listingId.value.trim()
          ? this.channels.invalidListingIdMessage(entry.platform)
          : 'Please enter your listing ID';
      }
    }
    if (invalid) {
      this.cdr.markForCheck();
      return;
    }
    this.submitting = true;
    this.cdr.markForCheck();
    const needed: ListingSetupEntry[] = [];
    const handled: ListingSetupEntry[] = [];
    const toCreate: ListingSetupEntry[] = [];
    const toConnect: NewChannelListing[] = [];
    try {
      // One write for the whole save, made even when a later listing fails: a created
      // listing is never created again on retry, so its connect item has no second chance.
      try {
        for (const entry of entries) {
          if (entry.has) {
            await this.saveListing(entry, toConnect);
            needed.push(entry);
          } else if (!entry.existing) {
            handled.push(entry);
            if (!entry.conciergeSetup) {
              toCreate.push(entry);
            }
          }
        }
      } finally {
        await this.setupService.addListingSetupItems(toConnect, toCreate.map((entry) => ({
          addressId: entry.addressId,
          platform: entry.platform,
          addressName: entry.addressName,
        })));
      }
      // The items before the markers: a retry skips an item it already added, but
      // nothing re-adds one once the marker says TIDY is on it.
      for (const entry of toCreate) {
        await this.setupService.markConciergeSetup(entry.addressId, entry.platform);
        entry.conciergeSetup = true;
      }
      await this.setupService.markListingsStepCompleted(this.completedAddressIds());
      this.buildSummary(needed, handled);
      this.state = 'summary';
      // Mobile keeps the Listings tab mounted under this route, so tell it to re-read.
      this.channels.listingsChanged$.next();
    } catch (err: any) {
      this.util.showError(err?.error?.message || err?.message || 'Could not save your listings. Please try again.');
    } finally {
      this.submitting = false;
      this.cdr.markForCheck();
    }
  };

  // Creates the listing, or updates the ID of one that already exists. Successful creates
  // are remembered on the entry so a retry after a later failure doesn't duplicate them,
  // and added to toConnect for TIDY's action items.
  private async saveListing(entry: ListingSetupEntry, toConnect: NewChannelListing[]): Promise<void> {
    const platformId = this.channels.parseListingId(entry.listingId.value, entry.platform)!;
    if (entry.existing) {
      if (platformId !== entry.existing.platform_id) {
        const updated: any = await this.client.updateAddressListing(entry.existing.id, { platform_id: platformId });
        entry.existing = { ...entry.existing, ...(updated || {}), platform_id: platformId };
      }
    } else {
      const created: any = await this.client.createAddressListing({
        address_id: entry.addressId,
        platform: entry.platform,
        platform_id: platformId,
      });
      entry.existing = { status: 'connecting', ...(created || {}), platform_id: platformId } as AddressListing;
      entry.locked = true;
      toConnect.push({
        id: created?.id,
        platform: entry.platform,
        addressName: entry.addressName,
      });
    }
    if (entry.conciergeSetup) {
      await this.setupService.clearConciergeSetup(entry.addressId, entry.platform);
      entry.conciergeSetup = false;
    }
  }

  // The full page covers every address, so it completes the action item everywhere. The
  // mini page completes its address only once all three channels have an answer.
  private completedAddressIds(): number[] {
    if (this.mode === 'all') {
      return this.addresses.map((a) => a.id);
    }
    const entry = this.entry!;
    const listings = this.listingsByAddress.get(entry.addressId) || [];
    const markers = this.setupState.get(entry.addressId)?.conciergePlatforms ?? new Set<string>();
    const answered = STR_CHANNEL_PLATFORMS.every((platform) =>
      platform === entry.platform
        ? !!entry.existing || entry.conciergeSetup
        : listings.some((l) => l.platform === platform) || markers.has(platform));
    return answered ? [entry.addressId] : [];
  }

  // ---------- Summary ----------

  private buildSummary(needed: ListingSetupEntry[], handled: ListingSetupEntry[]): void {
    // A listing that's already past `connecting` needs nothing more from the client.
    this.summaryNeeded = needed
      .filter((e) => !e.existing?.status || e.existing.status === 'connecting')
      .map((e) => {
        const waiting = getWaitingOnYou(e.platform);
        return {
          addressId: e.addressId,
          platform: e.platform,
          platformLabel: e.platformLabel,
          logo: e.logo,
          addressName: e.addressName,
          title: waiting.title,
          detail: waiting.detail,
          cohostUrl: this.cohostUrl(e),
          contactPrompt: getContactPrompt(e.platform) || `Send it to your account manager, ${this.managerName}.`,
        };
      });
    this.summaryHandled = handled.map((e) => ({
      addressId: e.addressId,
      platform: e.platform,
      platformLabel: e.platformLabel,
      logo: e.logo,
      addressName: e.addressName,
      title: `We’ll set up your ${e.platformLabel} listing`,
      detail: 'Your account manager will reach out if we need anything from you.',
      cohostUrl: null,
      contactPrompt: '',
    }));
    if (this.mode === 'single' && this.answer === 'no' && this.entry) {
      this.summaryTitle = 'Okay — we’ll set it up for you';
      this.summaryIntro = `TIDY will create your ${this.entry.platformLabel} listing for ${this.entry.addressName}. You’ll see it as “TIDY is creating your listing” on your Listings page.`;
    } else if (!this.summaryNeeded.length) {
      this.summaryTitle = 'Thanks! We’ll take it from here';
      this.summaryIntro = this.summaryHandled.length ? 'Nothing is needed from you right now.' : '';
    } else {
      this.summaryTitle = 'Thanks! Here’s what happens next';
      this.summaryIntro = 'A few things we’re waiting on from you — we take care of everything else.';
    }
  }

  private cohostUrl(entry: ListingSetupEntry): string | null {
    if (entry.platform !== 'airbnb') { return null; }
    const id = this.channels.parseAirbnbListingId(entry.existing?.platform_id || entry.listingId.value || '');
    return id ? `https://www.airbnb.com/hosting/listings/editor/${id}/details/co-hosts` : null;
  }

  copyEmail = () => {
    this.util.copyToClipBoard(this.cohostEmail);
  };

  callManager(): void {
    window.location.href = `tel:${this.managerPhone}`;
  }

  emailManager(): void {
    window.location.href = `mailto:${this.managerEmail}`;
  }

  textManager(): void {
    window.location.href = `sms:${this.managerPhone}`;
  }

  openCohostPage(url: string | null): void {
    if (url) { this.util.openUrl(url); }
  }

  // OK on the summary lands on the Listings page. The property Listings editor gets its
  // own page back instead, since that's where the user started.
  finish = () => {
    if (this.returnRoute === 'property-listings' && this.addressId) {
      if (this.windowService.isDesktopRes) {
        this.rightSidePanelService.setDialogPageTitle('Listings');
        this.rightSidePanelService.navigateTo('property-listings', { addressId: this.addressId });
      } else {
        this.navCtrl.navigateBack('property-listings');
      }
      return;
    }
    const listingsPage = 'listings';
    if (this.windowService.isDesktopRes) {
      this.rightSidePanelService.closeRightSidePanel();
      this.navCtrl.navigateForward(listingsPage);
    } else if (this.returnRoute === 'listings') {
      this.navCtrl.navigateBack(listingsPage);
    } else {
      this.navCtrl.navigateForward(listingsPage);
    }
  };
}
